/**
 * Página de Configurações do Admin
 * Permite configurar branding (logo, ícone, nome do sistema) e outras configurações globais
 * Inclui configuração do widget de suporte interno
 */

import { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Settings, Upload, Image, Loader2, Trash2, MessageCircle, Building2, Inbox, Monitor } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { useSystemSettings } from '@/hooks/useSystemSettings';
import { useSupportWidgetConfig } from '@/hooks/useSupportWidgetConfig';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

const AdminSettings = () => {
  const { toast } = useToast();
  const { branding, isLoading, updateBranding, isUpdating, uploadBrandingImage } = useSystemSettings();
  
  // Estados locais para preview durante upload
  const [appName, setAppName] = useState<string>('');
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const [loginLogoPreview, setLoginLogoPreview] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingIcon, setIsUploadingIcon] = useState(false);
  const [isUploadingLoginLogo, setIsUploadingLoginLogo] = useState(false);
  
  // Refs para inputs de arquivo
  const logoInputRef = useRef<HTMLInputElement>(null);
  const iconInputRef = useRef<HTMLInputElement>(null);
  const loginLogoInputRef = useRef<HTMLInputElement>(null);

  // Inicializar nome do app quando branding carregar
  useState(() => {
    if (branding?.app_name && !appName) {
      setAppName(branding.app_name);
    }
  });

  // Handler para upload de logo
  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validar tipo de arquivo
    if (!['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'].includes(file.type)) {
      toast({
        title: 'Formato inválido',
        description: 'Use imagens PNG, JPG, SVG ou WebP.',
        variant: 'destructive',
      });
      return;
    }
    
    // Validar tamanho (máx 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: 'Arquivo muito grande',
        description: 'O tamanho máximo é 2MB.',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      setIsUploadingLogo(true);
      
      // Preview local
      const reader = new FileReader();
      reader.onload = (ev) => setLogoPreview(ev.target?.result as string);
      reader.readAsDataURL(file);
      
      // Upload para storage
      const logoUrl = await uploadBrandingImage(file, 'logo');
      
      // Atualizar configuração
      await updateBranding({ logo_url: logoUrl });
      setLogoPreview(null);
    } catch (error) {
      console.error('Erro ao fazer upload da logo:', error);
      setLogoPreview(null);
    } finally {
      setIsUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  // Handler para upload de ícone
  const handleIconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validar tipo de arquivo
    if (!['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'].includes(file.type)) {
      toast({
        title: 'Formato inválido',
        description: 'Use imagens PNG, JPG, SVG ou WebP.',
        variant: 'destructive',
      });
      return;
    }
    
    // Validar tamanho (máx 1MB)
    if (file.size > 1024 * 1024) {
      toast({
        title: 'Arquivo muito grande',
        description: 'O tamanho máximo é 1MB.',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      setIsUploadingIcon(true);
      
      // Preview local
      const reader = new FileReader();
      reader.onload = (ev) => setIconPreview(ev.target?.result as string);
      reader.readAsDataURL(file);
      
      // Upload para storage
      const iconUrl = await uploadBrandingImage(file, 'icon');
      
      // Atualizar configuração
      await updateBranding({ icon_url: iconUrl });
      setIconPreview(null);
    } catch (error) {
      console.error('Erro ao fazer upload do ícone:', error);
      setIconPreview(null);
    } finally {
      setIsUploadingIcon(false);
      if (iconInputRef.current) iconInputRef.current.value = '';
    }
  };

  // Handler para remover logo
  const handleRemoveLogo = async () => {
    try {
      await updateBranding({ logo_url: null });
    } catch (error) {
      console.error('Erro ao remover logo:', error);
    }
  };

  // Handler para remover ícone
  const handleRemoveIcon = async () => {
    try {
      await updateBranding({ icon_url: null });
    } catch (error) {
      console.error('Erro ao remover ícone:', error);
    }
  };

  // Handler para upload da logo da tela de login
  const handleLoginLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validar tipo de arquivo
    if (!['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'].includes(file.type)) {
      toast({
        title: 'Formato inválido',
        description: 'Use imagens PNG, JPG, SVG ou WebP.',
        variant: 'destructive',
      });
      return;
    }
    
    // Validar tamanho (máx 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: 'Arquivo muito grande',
        description: 'O tamanho máximo é 2MB.',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      setIsUploadingLoginLogo(true);
      
      // Preview local
      const reader = new FileReader();
      reader.onload = (ev) => setLoginLogoPreview(ev.target?.result as string);
      reader.readAsDataURL(file);
      
      // Upload para storage
      const loginLogoUrl = await uploadBrandingImage(file, 'login_logo');
      
      // Atualizar configuração
      await updateBranding({ login_logo_url: loginLogoUrl });
      setLoginLogoPreview(null);
    } catch (error) {
      console.error('Erro ao fazer upload da logo de login:', error);
      setLoginLogoPreview(null);
    } finally {
      setIsUploadingLoginLogo(false);
      if (loginLogoInputRef.current) loginLogoInputRef.current.value = '';
    }
  };

  // Handler para remover logo da tela de login
  const handleRemoveLoginLogo = async () => {
    try {
      await updateBranding({ login_logo_url: null });
    } catch (error) {
      console.error('Erro ao remover logo de login:', error);
    }
  };

  // Handler para salvar nome do app
  const handleSaveAppName = async () => {
    if (!appName.trim()) {
      toast({
        title: 'Nome inválido',
        description: 'O nome do sistema não pode estar vazio.',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      await updateBranding({ app_name: appName.trim() });
    } catch (error) {
      console.error('Erro ao salvar nome:', error);
    }
  };

  // URL da imagem atual (preview local ou do storage)
  const currentLogoUrl = logoPreview || branding?.logo_url;
  const currentIconUrl = iconPreview || branding?.icon_url;
  const currentLoginLogoUrl = loginLogoPreview || branding?.login_logo_url;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Controle de aba ativa via ?tab= (deep-link friendly)
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const activeTab: 'geral' | 'suporte' = rawTab === 'suporte' ? 'suporte' : 'geral';
  const handleTabChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'geral') next.delete('tab');
    else next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground">Configurações</h1>
        <p className="text-muted-foreground">Configure o sistema UaizyChat</p>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList>
          <TabsTrigger value="geral">Geral</TabsTrigger>
          <TabsTrigger value="suporte">Suporte</TabsTrigger>
        </TabsList>

        <TabsContent value="geral" className="mt-4 space-y-6">


      {/* Card de Identidade Visual */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Image className="h-5 w-5" />
            Identidade Visual
          </CardTitle>
          <CardDescription>
            Configure a logo, ícone e nome do sistema exibidos no login e menus
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Nome do Sistema */}
          <div className="space-y-2">
            <Label htmlFor="app-name">Nome do Sistema</Label>
            <div className="flex gap-2">
              <Input
                id="app-name"
                value={appName || branding?.app_name || ''}
                onChange={(e) => setAppName(e.target.value)}
                placeholder="UaizyChat"
                className="max-w-xs"
              />
              <Button 
                onClick={handleSaveAppName} 
                disabled={isUpdating || appName === branding?.app_name}
                size="sm"
              >
                {isUpdating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Salvar'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Exibido no login e no menu lateral
            </p>
          </div>

          {/* Logo Completa */}
          <div className="space-y-2">
            <Label>Logo Completa</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Recomendado: 200x50px, PNG ou SVG com fundo transparente
            </p>
            
            <div className="flex items-start gap-4">
              {/* Preview da Logo */}
              <div className="w-48 h-16 border border-dashed border-border rounded-lg flex items-center justify-center bg-muted/30 overflow-hidden">
                {currentLogoUrl ? (
                  <img 
                    src={currentLogoUrl} 
                    alt="Logo" 
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <span className="text-xs text-muted-foreground">Sem logo</span>
                )}
              </div>
              
              {/* Botões de ação */}
              <div className="flex flex-col gap-2">
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  className="hidden"
                  onChange={handleLogoUpload}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={isUploadingLogo}
                >
                  {isUploadingLogo ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  {currentLogoUrl ? 'Substituir' : 'Upload'}
                </Button>
                
                {branding?.logo_url && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRemoveLogo}
                    disabled={isUpdating}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remover
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Logo da Tela de Login */}
          <div className="space-y-2">
            <Label>Logo da Tela de Login</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Recomendado: 300x100px, PNG ou SVG com fundo transparente. Se não configurada, será usada a Logo Completa.
            </p>
            
            <div className="flex items-start gap-4">
              {/* Preview da Logo de Login */}
              <div className="w-48 h-16 border border-dashed border-border rounded-lg flex items-center justify-center bg-muted/30 overflow-hidden">
                {currentLoginLogoUrl ? (
                  <img 
                    src={currentLoginLogoUrl} 
                    alt="Logo Login" 
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <span className="text-xs text-muted-foreground">Sem logo de login</span>
                )}
              </div>
              
              {/* Botões de ação */}
              <div className="flex flex-col gap-2">
                <input
                  ref={loginLogoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  className="hidden"
                  onChange={handleLoginLogoUpload}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loginLogoInputRef.current?.click()}
                  disabled={isUploadingLoginLogo}
                >
                  {isUploadingLoginLogo ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  {currentLoginLogoUrl ? 'Substituir' : 'Upload'}
                </Button>
                
                {branding?.login_logo_url && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRemoveLoginLogo}
                    disabled={isUpdating}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remover
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Ícone */}
          <div className="space-y-2">
            <Label>Ícone</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Recomendado: 64x64px, quadrado, PNG ou SVG
            </p>
            
            <div className="flex items-start gap-4">
              {/* Preview do Ícone */}
              <div className="w-16 h-16 border border-dashed border-border rounded-lg flex items-center justify-center bg-muted/30 overflow-hidden">
                {currentIconUrl ? (
                  <img 
                    src={currentIconUrl} 
                    alt="Ícone" 
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <span className="text-xs text-muted-foreground">Sem ícone</span>
                )}
              </div>
              
              {/* Botões de ação */}
              <div className="flex flex-col gap-2">
                <input
                  ref={iconInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  className="hidden"
                  onChange={handleIconUpload}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => iconInputRef.current?.click()}
                  disabled={isUploadingIcon}
                >
                  {isUploadingIcon ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  {currentIconUrl ? 'Substituir' : 'Upload'}
                </Button>
                
                {branding?.icon_url && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRemoveIcon}
                    disabled={isUpdating}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remover
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Preview combinado */}
          <div className="pt-4 border-t border-border">
            <Label className="mb-3 block">Preview</Label>
            <div className="flex items-center gap-6">
              {/* Preview menu expandido */}
              <div className="bg-sidebar border border-border rounded-lg p-3 flex items-center gap-2">
                {currentIconUrl ? (
                  <img src={currentIconUrl} alt="" className="w-8 h-8 object-contain" />
                ) : (
                  <div className="w-8 h-8 bg-primary rounded flex items-center justify-center text-primary-foreground font-bold text-sm">
                    {(appName || branding?.app_name || 'U')[0].toUpperCase()}
                  </div>
                )}
                <span className="font-semibold text-sidebar-foreground">
                  {appName || branding?.app_name || 'UaizyChat'}
                </span>
              </div>
              
              {/* Preview menu retraído */}
              <div className="bg-sidebar border border-border rounded-lg p-2">
                {currentIconUrl ? (
                  <img src={currentIconUrl} alt="" className="w-8 h-8 object-contain" />
                ) : (
                  <div className="w-8 h-8 bg-primary rounded flex items-center justify-center text-primary-foreground font-bold text-sm">
                    {(appName || branding?.app_name || 'U')[0].toUpperCase()}
                  </div>
                )}
              </div>
              
              <span className="text-xs text-muted-foreground">← Menu retraído</span>
            </div>
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="suporte" className="mt-4">
          {/* Card de outras configurações - Widget de Suporte */}
          <SupportWidgetSettings />
        </TabsContent>
      </Tabs>
    </div>
  );
};

/**
 * Componente para configurar o widget de suporte interno
 * Permite selecionar qual tenant/inbox receberá as mensagens de suporte
 */
function SupportWidgetSettings() {
  const { toast } = useToast();
  const { config: supportConfig, updateConfig: updateSupportConfig, isUpdating: isSavingSupport } = useSupportWidgetConfig();
  
  // Estados para seleção de tenant/inbox
  const [selectedTenant, setSelectedTenant] = useState<string>('');
  const [selectedInbox, setSelectedInbox] = useState<string>('');
  
  // Estado para acesso remoto (WebRTC)
  const [remoteAccessEnabled, setRemoteAccessEnabled] = useState<boolean>(false);
  
  // Sincroniza estados locais com configuração salva
  useEffect(() => {
    if (supportConfig.tenant_id && !selectedTenant) {
      setSelectedTenant(supportConfig.tenant_id);
    }
    if (supportConfig.inbox_id && !selectedInbox) {
      setSelectedInbox(supportConfig.inbox_id);
    }
  }, [supportConfig.tenant_id, supportConfig.inbox_id, selectedTenant, selectedInbox]);
  
  // Sincroniza estado de acesso remoto com configuração salva
  useEffect(() => {
    if (supportConfig.remote_access_enabled !== undefined) {
      setRemoteAccessEnabled(supportConfig.remote_access_enabled);
    }
  }, [supportConfig.remote_access_enabled]);
  
  // Query para buscar todos os tenants (admin tem acesso a todos)
  const { data: tenants, isLoading: isLoadingTenants } = useQuery({
    queryKey: ['admin-tenants-for-widget'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tenants')
        .select('id, name')
        .order('name');
      if (error) throw error;
      return data;
    },
  });
  
  // Tenant ativo para buscar inboxes (usa selecionado ou salvo)
  const activeTenantId = selectedTenant || supportConfig.tenant_id;
  
  // Query para buscar inboxes do tenant selecionado
  const { data: inboxes, isLoading: isLoadingInboxes } = useQuery({
    queryKey: ['tenant-inboxes-for-widget', activeTenantId],
    queryFn: async () => {
      if (!activeTenantId) return [];
      const { data, error } = await supabase
        .from('inboxes')
        .select('id, name')
        .eq('tenant_id', activeTenantId)
        .eq('status', 'active')
        .order('name');
      if (error) throw error;
      return data;
    },
    enabled: !!activeTenantId,
  });
  
  // Handler para salvar configuração do widget de suporte
  const handleSaveSupportWidget = async () => {
    const tenant = tenants?.find(t => t.id === selectedTenant);
    const inbox = inboxes?.find(i => i.id === selectedInbox);
    
    if (!selectedInbox) {
      toast({
        title: 'Selecione uma inbox',
        description: 'É necessário selecionar uma inbox para o widget de suporte.',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      await updateSupportConfig({
        enabled: true,
        tenant_id: selectedTenant,
        inbox_id: selectedInbox,
        tenant_name: tenant?.name || null,
        inbox_name: inbox?.name || null,
      });
    } catch (error) {
      // Erro já tratado pelo hook
    }
  };
  
  // Handler para desabilitar o widget
  const handleDisableWidget = async () => {
    try {
      await updateSupportConfig({
        enabled: false,
      });
      toast({
        title: 'Widget desabilitado',
        description: 'O widget de suporte foi desabilitado.',
      });
    } catch (error) {
      // Erro já tratado pelo hook
    }
  };
  
  // Handler para toggle de acesso remoto (WebRTC)
  const handleRemoteAccessToggle = async (checked: boolean) => {
    try {
      await updateSupportConfig({
        remote_access_enabled: checked,
      });
      setRemoteAccessEnabled(checked);
    } catch (error) {
      // Erro já tratado pelo hook
    }
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Outras Configurações
        </CardTitle>
        <CardDescription>
          Configurações adicionais do sistema
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Widget de Suporte Interno */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-primary" />
            <Label className="text-base font-medium">Widget de Suporte Interno</Label>
          </div>
          <p className="text-sm text-muted-foreground">
            Configure qual tenant e inbox receberá as mensagens de suporte dos usuários do sistema.
            O widget aparecerá em todas as páginas do tenant (Conversas e CRM).
          </p>
          
          <div className="grid gap-4 sm:grid-cols-2">
            {/* Select de Tenant */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5" />
                Tenant
              </Label>
              <Select
                value={selectedTenant}
                onValueChange={(value) => {
                  setSelectedTenant(value);
                  setSelectedInbox(''); // Reset inbox ao trocar tenant
                }}
                disabled={isLoadingTenants}
              >
                <SelectTrigger>
                  <SelectValue placeholder={isLoadingTenants ? 'Carregando...' : 'Selecione o tenant'} />
                </SelectTrigger>
                <SelectContent>
                  {tenants?.map((tenant) => (
                    <SelectItem key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Select de Inbox */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <Inbox className="h-3.5 w-3.5" />
                Inbox
              </Label>
              <Select
                value={selectedInbox}
                onValueChange={setSelectedInbox}
                disabled={!activeTenantId || isLoadingInboxes}
              >
                <SelectTrigger>
                  <SelectValue placeholder={
                    !activeTenantId 
                      ? 'Selecione o tenant primeiro' 
                      : isLoadingInboxes 
                        ? 'Carregando...' 
                        : 'Selecione a inbox'
                  } />
                </SelectTrigger>
                <SelectContent>
                  {inboxes?.map((inbox) => (
                    <SelectItem key={inbox.id} value={inbox.id}>
                      {inbox.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Status atual e botões de ação */}
          <div className="flex items-center justify-between pt-2 border-t">
            <div className="text-sm">
              {supportConfig.enabled && supportConfig.inbox_name ? (
                <p className="text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium mr-2">
                    Ativo
                  </span>
                  <strong>{supportConfig.tenant_name}</strong> → <strong>{supportConfig.inbox_name}</strong>
                </p>
              ) : (
                <p className="text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-xs font-medium mr-2">
                    Inativo
                  </span>
                  Nenhuma configuração ativa
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {supportConfig.enabled && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDisableWidget}
                  disabled={isSavingSupport}
                >
                  Desabilitar
                </Button>
              )}
              <Button
                onClick={handleSaveSupportWidget}
                disabled={!selectedInbox || isSavingSupport}
                size="sm"
              >
                {isSavingSupport ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Salvar
              </Button>
            </div>
          </div>
        </div>
        
        {/* Seção de Acesso Remoto (WebRTC) */}
        <div className="space-y-4 pt-6 border-t">
          <div className="flex items-center gap-2">
            <Monitor className="h-5 w-5 text-primary" />
            <Label className="text-base font-medium">Acesso Remoto (WebRTC)</Label>
          </div>
          <p className="text-sm text-muted-foreground">
            Permite que atendentes do tenant de suporte solicitem visualização da tela dos usuários durante o atendimento.
            <br />
            <span className="text-xs text-warning">⚠️ Requer tenant de suporte configurado acima.</span>
          </p>
          
          <div className="flex items-center justify-between py-3 px-4 rounded-lg border bg-muted/30">
            <div className="space-y-0.5">
              <Label className="text-sm font-medium">Habilitar botão de Acesso Remoto</Label>
              <p className="text-xs text-muted-foreground">
                Quando ativo, um botão de "Visualizar Tela" aparece no chat do tenant de suporte.
              </p>
            </div>
            <Switch
              checked={remoteAccessEnabled}
              onCheckedChange={handleRemoteAccessToggle}
              disabled={!supportConfig.enabled || isSavingSupport}
            />
          </div>
          
          {!supportConfig.enabled && (
            <p className="text-xs text-muted-foreground italic">
              ℹ️ Configure e ative o Widget de Suporte acima para habilitar o Acesso Remoto.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default AdminSettings;
